<template>
    <div>
		<page-loader v-if="!asset" />
		<div v-else>
			<AssetDetailsHeader
				:asset="asset"
				@assign="showAssignAssetDialog = true"
				@unassign="showUnassignDialog = true"
				@delete="showConfirmDeleteDialog = true"
				@duplicate="showDuplicateDialog = true"
				@retire="showRetireDialog = true"
				@activate="showActivateDialog = true"
				@service="showServiceDialog = true"/>

			<div class="mt-4">
				<AssetDetailsInfo
					:asset="asset"
					@edit="showAssetEditorDialog = true"
					@service="showServiceDialog = true"/>
			</div>

			<assign-asset-dialog
				:dialog="showAssignAssetDialog"
				:asset="asset"
				:is-saving="isAssigning"
				@closeDialog="showAssignAssetDialog = false"
				@onSave="confirmAssign"/>

			<confirmation-dialog
				:dialog="showConfirmDeleteDialog"
				title="Confirm Delete"
				text="Are you sure you want to delete this asset?"
				:isConfirming="isDeleting"
				@onConfirm="confirmDelete"
				@onCancel="showConfirmDeleteDialog = false"/>

			<confirmation-dialog
				v-if="showUnassignDialog"
				:dialog="showUnassignDialog"
				title="Confirm Unassign"
				:text="`Are you sure you want to unassign this asset from ${this.asset.assignedTo.firstName} ${this.asset.assignedTo.lastName}?`"
				:isConfirming="isSaving"
				has-note-field
				@onConfirm="confirmUnassign"
				@onCancel="showUnassignDialog = false"/>

			<confirmation-dialog
				v-if="showRetireDialog"
				:dialog="showRetireDialog"
				title="Confirm Retire"
				text="Are you sure you want to retire this asset?"
				:isConfirming="isSaving"
				has-note-field
				@onConfirm="confirmRetire"
				@onCancel="showRetireDialog = false"/>

			<confirmation-dialog
				v-if="showActivateDialog"
				:dialog="showActivateDialog"
				title="Confirm Un-retire"
				text="Are you sure you want to un-retire this asset?"
				:isConfirming="isSaving"
				has-note-field
				@onConfirm="confirmActivate"
				@onCancel="showActivateDialog = false"/>

			<confirmation-dialog
				:dialog="showDuplicateDialog"
				title="Confirm Duplicate"
				text="Are you sure you want to duplicate this asset?"
				:isConfirming="isSaving"
				@onConfirm="confirmDuplicate"
				@onCancel="showDuplicateDialog = false"/>

			<asset-editor-dialog
				v-if="showAssetEditorDialog"
				:dialog="showAssetEditorDialog"
				:asset="asset"
				@closeDialog="showAssetEditorDialog = false" />

			<asset-service-dialog
				v-if="showServiceDialog"
				:dialog="showServiceDialog"
				:is-confirming="isSaving"
				@onConfirm="confirmServiceAsset"
				@onCancel="showServiceDialog = false"/>

		</div>
    </div>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex';
	import PageLoader from '@components/PageLoader.vue';
	import AssetDetailsHeader from '@components/assets/AssetDetailsHeader.vue';
	import AssetDetailsInfo from '@components/assets/AssetDetailsInfo.vue';
	import AssignAssetDialog from '@components/assets/AssignAssetDialog.vue';
	import AssetEditorDialog from '@components/assets/AssetEditorDialog.vue';
	import AssetServiceDialog from '@components/assets/AssetServiceDialog.vue';
	import toast from '@/services/toasts/index.js';

export default {
	components: { AssetServiceDialog, AssetEditorDialog, AssignAssetDialog, AssetDetailsInfo, AssetDetailsHeader, PageLoader },
	data() {
		return {
			loading: false,
			showAssignAssetDialog: false,
			showConfirmDeleteDialog: false,
			showUnassignDialog: false,
			showRetireDialog: false,
			showAssetEditorDialog: false,
			showDuplicateDialog: false,
			showActivateDialog: false,
			showServiceDialog: false
		}
	},
	computed: {
		...mapGetters({
			isAssigning: 'assets/isAssigning',
			isDeleting: 'assets/isDeleting',
			isSaving: 'assets/isSaving',
			hasLoadedAssets: 'assets/hasLoaded',
			hasLoadedAssetConfiguration: 'assetConfiguration/hasLoaded',
			getAssetById: 'assets/getById'
		}),
		asset() {
			return this.getAssetById(this.$route.params.id);
		}
	},
	methods: {
		...mapActions({
			loadAssetConfig: 'assetConfiguration/get',
			fetchDetailsById: 'assets/getDetailsById',
			assignAsset: 'assets/assign',
			unassignAsset: 'assets/unassign',
			deleteAsset: 'assets/delete',
			retireAsset: 'assets/retire',
			duplicateAsset: 'assets/duplicate',
			activateAsset: 'assets/activate',
			serviceAsset: 'assets/service'
		}),
		async confirmAssign(payload) {
			const result = await this.assignAsset({ ...payload, assetIds: [this.asset.id] })
			if (result.isSuccess) {
				this.showAssignAssetDialog = false;
				toast.success('Asset assigned successfully.');
			}
		},
		async confirmUnassign(note) {
			const result = await this.unassignAsset({assetIds: [this.asset.id], notes: note})
			if (result.isSuccess) {
				this.showUnassignDialog = false;
			}
		},
		async confirmRetire(note) {
			const result = await this.retireAsset({assetIds: [this.asset.id], notes: note})
			if (result.isSuccess) {
				this.showRetireDialog = false;
			}
		},
		async confirmActivate(note) {
			const result = await this.activateAsset({assetIds: [this.asset.id], notes: note})
			if (result.isSuccess) {
				this.showActivateDialog = false;
			}
		},
		async confirmDuplicate() {
			const result = await this.duplicateAsset(this.asset.id)
			if (result.isSuccess) {
				this.showRetireDialog = false;
				this.$router.push({name: 'asset', params: { id: result.data.id }})
			}
		},
		async confirmDelete() {
			const result = await this.deleteAsset(this.asset.id);
			if (result.isSuccess) {
				this.deletingId = null;
				this.showConfirmDeleteDialog = false;
				this.$router.push({name: 'assets'});
			}
		},
		async confirmServiceAsset(confirmEvent) {
			const response = await this.serviceAsset({
				assetIds: [this.asset.id],
				serviceDate: confirmEvent.serviceDate,
				nextServiceDate: confirmEvent.nextServiceDate,
				notes: confirmEvent.note
			})

			if (response.isSuccess) {
				this.serviceDate = null
				this.showServiceDialog = false
			}
		},
	},
	async created() {
		this.loading = true;

		await this.fetchDetailsById(this.$route.params.id);

		if (!this.hasLoadedAssetConfiguration) await this.loadAssetConfig()

		this.loading = false;
	}
}

</script>

<style scoped>
	#assetInfoContainer {
		display: flex;

	}
</style>